import { ecId, ecLean, itemField } from '../ability/context';
import { compilePossessor, possessorDependencyView } from '../compile/possessor-compile';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { applyStatusRootFields } from '../compile/status-root-residue';
import { applyResourceValues } from '../resources/resource-adjust';
import { retainHeld } from './memory-pickup';
import type { InventoryEquipCommand } from './equip-plan';
import { applyMemberStatusRoots, recompileMemberStatuses, type MemberStatusStep } from '../compile/member-status-recompile';
import { appliedStatusRows } from '../transport/applied-status-rows';
import { statusRows } from '../statuses/expiry';

type Row = Record<string, unknown>;
const row = (value: unknown): Row => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : {};
const upper = (value: unknown): string => String(value ?? '').toUpperCase();

/** Save the equip lane's decision and update only its carrier's affected clusters. Preparation never changes held state. */
export function prepareMemoryEquipment(base: Row, surface: Row, command: InventoryEquipCommand): () => Row {
    const fail = (reason: string): never => { throw new Error(`Equipment could not be changed: ${reason}`); };
    const actorId = upper(command.actor_id);
    const actor = (base.encounter_creatures_arr as Row[]).find(member => upper(member.entity_id) === actorId);
    const displayed = (surface.encounter_creatures_arr as Row[]).find(member => upper(ecId(member)) === actorId);
    if (!actor || !displayed) return fail('the character is not in this room.');
    const saved = readInventoryRows(actor, 'inventory');
    const find = (id: string): Row | undefined => saved.find(node => upper(readInventoryField(node, 'inventory_id')) === upper(id));
    for (const before of command.before.rows) {
        const held = find(before.inventory_id);
        if (!held || upper(readInventoryField(held, 'possess_id')) !== upper(before.possess_id)
            || upper(readInventoryField(held, 'equipped')) !== upper(before.equipped)
            || before.item_qty !== undefined && Number(readInventoryField(held, 'item_qty') ?? 1) !== before.item_qty) return fail('the equipment changed; try again.');
    }
    const aps = Number(ecLean(actor, 'aps') ?? 0);
    if (command.ap.in_combat && !command.timed_finish && aps !== Number(command.before.aps ?? 0)) return fail('the available AP changed; try again.');
    const planned = command.plan.rows;
    if ((!planned.length && !command.timed) || planned.some(change => !find(change.inventory_id)
        || upper(readInventoryField(find(change.inventory_id) ?? null, 'possess_id')) !== actorId)) return fail('the item is not carried by this character.');
    const primary = planned.find(change => upper(change.inventory_id) === upper(command.inventory_id));
    if (!find(command.inventory_id) || !command.timed && !primary) return fail('the selected item is not held.');
    if (command.timed_finish && (planned.some(change => change.split_from || change.inventory_deleted || change.item_qty !== undefined)
        || command.timed_finish.boundary || command.timed_finish.split_statuses?.length)) return fail('timed-equipment:split-merge-or-loss-effects');
    const catalog = row(base.catalog);
    const definition = (id: string): Row | null => catalog[upper(id)] ? row(catalog[upper(id)]) : null;
    const trial = possessorDependencyView(displayed);
    let added: Row | null = null, removed: Row | null = null;
    if (command.timed) {
        const write = command.timed.activity.writes.find(write => write.table === 'statuses');
        if (!write) return fail('timed-equipment:status-plan-missing');
        const after = write.after;
        if (upper(after.status_target_id) !== actorId || upper(after.status_caster_id) !== actorId) return fail('timed-equipment:status-owner-not-held');
        const prior = statusRows(actor, 'target').find(status => upper(itemField(status, 'status_id')) === upper(write.id));
        if (write.before && (!prior || Object.entries(write.before).some(([key, value]) => String(itemField(prior, key) ?? '') !== String(value ?? ''))))
            return fail('timed-equipment:status-changed');
        const status = { ...after, status_id: upper(write.id), target: actorId, caster: actorId, ends: '' };
        added = { ...appliedStatusRows([status as Parameters<typeof appliedStatusRows>[0][number]], row(surface.campaign_arr))[0]?.row, ...after, status_id: upper(write.id) };
    } else if (command.timed_finish) {
        removed = statusRows(actor, 'target').find(status => upper(itemField(status, 'status_id')) === upper(command.timed_finish!.status_id)) ?? null;
        if (!removed || upper(itemField(removed, 'status_origin_id')) !== upper(command.inventory_id)) return fail('timed-equipment:status-changed');
    }
    if (added || removed) {
        const id = upper(itemField(added ?? removed, 'status_id'));
        const statusItemId = upper(itemField(added ?? removed, 'status_item_id'));
        const step: MemberStatusStep = added ? { kind: 'add', statusItemId, casterId: actorId, statusId: id, stack: 1 }
            : { kind: 'remove', statusItemId, statusId: id };
        const result = recompileMemberStatuses(trial, [step], { definition, catalog: row(trial.catalog),
            conditionScene: () => ({ encounter: surface, campaign: surface.campaign_arr }), roster: () => surface.encounter_creatures_arr as Row[] });
        if (!result.ok) return fail(`timed-equipment:${result.reason}`);
        row(trial.computed).creature = result.creature; row(trial.computed).vitality = result.vitality;
        if (result.catalog) trial.catalog = result.catalog;
        applyMemberStatusRoots(trial, result);
        trial.statuses = Object.fromEntries((['target', 'caster'] as const).map(mode => [mode,
            [...statusRows(displayed, mode).filter(status => upper(itemField(status, 'status_id')) !== id),
                ...added ? [{ ...added, status_arr: definition(statusItemId) }] : []]]));
    }
    const compiled = primary ? compilePossessor(trial, { kind: primary.equipped ? 'equip' : 'unequip',
        inventory_id: primary.inventory_id, equipped: primary.equipped,
        stow: planned.filter(change => change !== primary && !change.equipped).map(change => change.inventory_id),
        reslot: planned.filter(change => change !== primary && change.equipped).map(change => ({ inventory_id: change.inventory_id, equipped: change.equipped })) }, {
        catalog: row(trial.catalog), definition,
        conditionScene: () => ({ encounter: surface, campaign: surface.campaign_arr }), roster: () => surface.encounter_creatures_arr as Row[],
    }) : null;
    if (compiled && !compiled.ok) return fail(`the ${command.kind.replace(/_/g, ' ')} action is unavailable (${compiled.reason}).`);
    if (compiled?.ok) {
        trial.inventory = compiled.clusters.inventory; trial.catalog = compiled.derived.catalog;
        row(trial.computed).creature = compiled.derived['computed.creature']; row(trial.computed).vitality = compiled.derived['computed.vitality'];
        for (const [key, value] of Object.entries(compiled.derived.root_scalars ?? {})) {
            if (value === null) delete trial[key]; else trial[key] = value;
        }
        applyStatusRootFields(trial, compiled.derived.root_fields);
    }
    return () => {
        const computed = row(displayed.computed);
        const creature = retainHeld(computed.creature, row(trial.computed).creature);
        const vitality = retainHeld(computed.vitality, row(trial.computed).vitality);
        const paths = planned.length ? ['inventory'] : [];
        for (const key of new Set([...Object.keys(row(computed.creature)), ...Object.keys(row(creature))]))
            if (row(computed.creature)[key] !== row(creature)[key]) paths.push(`computed.creature.${key}`);
        if (computed.vitality !== vitality) paths.push('computed.vitality');
        displayed.inventory = retainHeld(displayed.inventory, trial.inventory);
        for (const change of planned) {
            const { inventory_id, split_from: _split, ...fields } = change;
            const values = command.timed_finish ? fields : { equipped: change.equipped };
            Object.assign(row(find(inventory_id)!.inventory), values);
            const projected = readInventoryRows(displayed, 'inventory').find(node => upper(readInventoryField(node, 'inventory_id')) === upper(inventory_id));
            if (projected) Object.assign(row(projected.inventory), values);
        }
        displayed.catalog = retainHeld(displayed.catalog, trial.catalog);
        computed.creature = creature; computed.vitality = vitality;
        for (const key of new Set([...Object.keys(displayed), ...Object.keys(trial)])) {
            if (['computed', 'catalog', 'inventory', 'encounter_creatures'].includes(key) || displayed[key] === trial[key]) continue;
            if (!(key in trial)) delete displayed[key]; else displayed[key] = trial[key];
            paths.push(key);
        }
        if (added || removed) {
            const statuses = row(actor.statuses), id = upper(itemField(added ?? removed, 'status_id'));
            for (const mode of ['target', 'caster'] as const) statuses[mode] = [...statusRows(actor, mode).filter(status => upper(itemField(status, 'status_id')) !== id), ...added ? [added] : []];
            actor.statuses = statuses;
        }
        const revision = Number(ecLean(actor, 'movement_rev') ?? 0) + (command.ap.cost > 0 ? 1 : 0);
        const afterAp = aps - command.ap.cost;
        for (const host of [actor, displayed]) {
            Object.assign(row(host.encounter_creatures), { movement_rev: revision, ...(command.ap.in_combat ? { aps: afterAp } : {}) });
            host.base_rev = Number(host.base_rev ?? 0) + 1;
        }
        if (command.ap.in_combat) {
            applyResourceValues(displayed, { aps: afterAp }, { layer: () => null, hasLayer: () => false });
            paths.push('encounter_creatures.aps', 'encounter_creatures.aps_used');
        }
        return { ok: true, applied: true, operation_id: command.operation_id, rev: revision, changed_paths: paths,
            ...(added ? { saved: { status: added } } : {}),
            frame: { schema: command.schema, operation_id: command.operation_id, actor_id: actorId, encounter_id: base.entity_id,
                kind: command.kind, rows: planned, aps: afterAp, committed: true,
                ...(removed ? { retired_status: { status_id: itemField(removed, 'status_id'), status_item_id: itemField(removed, 'status_item_id') } } : {}),
                ...(added ? { timed: { status: added.status_item_id, status_id: added.status_id, inventory_id: command.inventory_id } } : {}) } };
    };
}
